import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, X } from 'lucide-react';

export const useAdBlocker = () => {
  const [isAdBlockerActive, setIsAdBlockerActive] = useState(false);

  useEffect(() => {
    // We create a fake ad banner to detect if ad blockers block it
    const fakeAd = document.createElement('div');
    fakeAd.className = 'ad-banner adsbox doubleclick ad-zone sponsor-ad';
    fakeAd.style.position = 'absolute';
    fakeAd.style.left = '-999px';
    fakeAd.style.top = '-999px';
    fakeAd.style.height = '1px';
    fakeAd.style.width = '1px';
    
    // Add to body
    document.body.appendChild(fakeAd);

    // Timeout to give ad blockers time to act
    const timer = setTimeout(() => {
      // Some adblockers hide it, some remove it completely
      const isBlocked = 
        !document.body.contains(fakeAd) || 
        fakeAd.offsetHeight === 0 || 
        window.getComputedStyle(fakeAd).display === 'none';
        
      if (isBlocked) {
        setIsAdBlockerActive(true);
      }
      
      // Clean up
      if (document.body.contains(fakeAd)) {
        document.body.removeChild(fakeAd);
      }
    }, 500);

    return () => {
      clearTimeout(timer);
      if (document.body.contains(fakeAd)) {
        document.body.removeChild(fakeAd);
      }
    };
  }, []);

  return isAdBlockerActive;
};

const AdBlockerModal: React.FC = () => {
  const isAdBlockerActive = useAdBlocker();
  const [isOpen, setIsOpen] = useState(false);
  const [hasDismissed, setHasDismissed] = useState(false);

  useEffect(() => {
    // If ad blocker is detected and user hasn't dismissed yet in this session
    if (isAdBlockerActive && !hasDismissed && !sessionStorage.getItem('ad_blocker_dismissed')) {
      setIsOpen(true);
    }
  }, [isAdBlockerActive, hasDismissed]);

  const handleDismiss = () => {
    setIsOpen(false);
    setHasDismissed(true);
    sessionStorage.setItem('ad_blocker_dismissed', 'true');
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
            onClick={handleDismiss}
          />
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="relative w-full max-w-md bg-white dark:bg-slate-950 rounded-3xl p-6 shadow-2xl border border-slate-100 dark:border-slate-800"
          >
            <button 
              onClick={handleDismiss}
              className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
              title="Dismiss"
            >
              <X size={20} />
            </button>
            
            <div className="w-12 h-12 bg-rose-50 dark:bg-rose-900/30 text-rose-500 rounded-2xl flex items-center justify-center mb-6">
              <Heart size={24} className="fill-rose-500/20" />
            </div>

            <h3 className="text-xl font-bold font-serif text-slate-900 dark:text-white mb-2">
              Support Our Authors
            </h3>
            
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-6 leading-relaxed">
              We noticed you might be using an ad blocker. Literally relies on a zero-loss ad model where 50% of ad yields go directly to the authors. 
              <br /><br />
              Please consider disabling your ad blocker for this site to ensure creators get paid for their poetry, articles, and books.
            </p>

            <div className="flex gap-3">
              <button 
                onClick={handleDismiss}
                className="flex-1 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl transition-colors shadow-lg shadow-indigo-200 dark:shadow-none"
              >
                I Understand
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default AdBlockerModal;
